// Remove or comment out unused imports
import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
// import { ThemeProvider, createTheme } from '@mui/material/styles';
// import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Context
import { AuthProvider } from './context/AuthContext';
import { NotificationSettingsProvider } from './contexts/NotificationSettingsContext';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import AdminRoute from './components/auth/AdminRoute';
import CommercialRoute from './components/auth/CommercialRoute';
import ProjectsRoute from './components/auth/ProjectsRoute';
import SettingsRoute from './components/auth/SettingsRoute';
import MainLayout from './components/layout/MainLayout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ProjectManagerDashboard from './pages/ProjectManagerDashboard';
import Projects from './pages/Projects';
import CreateProject from './pages/projects/CreateProject';
import EditProject from './pages/projects/EditProject';
import ProjectDetail from './pages/projects/ProjectDetail';
import GanttChartPage from './pages/projects/GanttChartPage';
import GanttNewPage from './pages/projects/gantt-new';
import ResetPassword from './pages/ResetPassword';
import Settings from './pages/settings/Settings';
import ProfileSettings from './pages/settings/ProfileSettings';
import NotificationSettings from './pages/settings/NotificationSettings';
import NotificationPollingSettings from './pages/settings/NotificationPollingSettings';
import LanguageSettings from './pages/settings/LanguageSettings';
import AdminPanel from './pages/settings/admin/AdminPanel';
import PrivacyPolicy from './pages/PrivacyPolicy';
import RecruitmentForm from './pages/recruitment/RecruitmentForm';
import AdminRecruitmentList from './pages/admin/recruitment/AdminRecruitmentList';
import AdminRecruitmentDetail from './pages/admin/recruitment/AdminRecruitmentDetail';

// Master Job Pages
import MasterJobList from './pages/masterJobs/MasterJobList';

// New Pages
import Tasks from './pages/tasks/Tasks';
import Materials from './pages/materials/Materials';
import Teams from './pages/teams/Teams';
import Budget from './pages/budget/Budget';
import Documents from './pages/documents/Documents';
import Notifications from './pages/notifications/Notifications';

// Debug Components (only in development)
import TokenDebug from './components/debug/TokenDebug';

// Progress Tracking Pages
import UpdateProgress from './pages/progress/UpdateProgress';
import ApprovalProgress from './pages/progress/ApprovalProgress';
import ProgressSubmissionList from './pages/progress/ProgressSubmissionList';
import FundRequestList from './pages/fundRequests/FundRequestList';
import FundRequestForm from './pages/fundRequests/FundRequestForm';
import FundRequestDetail from './pages/fundRequests/FundRequestDetail';
import TransferSummary from './pages/fundRequests/TransferSummary';
import RekeningList from './pages/rekening/RekeningList';

// Layout wrapper component that uses MainLayout
const MainLayoutWrapper = () => {
  return (
    <MainLayout>
      <Outlet />
    </MainLayout>
  );
};

// Layout wrapper that keeps the top header but hides the sidebar for fullscreen content
const HeaderOnlyLayoutWrapper = () => {
  return (
    <MainLayout hideSidebar>
      <Outlet />
    </MainLayout>
  );
};

function App() {
  const router = createBrowserRouter([
    // Public routes (wrapper element)
    {
      element: <PublicRoute />,
      children: [
        { path: '/login', element: <Login /> },
        { path: '/reset-password/:token', element: <ResetPassword /> },
        { path: '/reset-password', element: <ResetPassword /> },
        { path: '/privacy-policy', element: <PrivacyPolicy /> },
        { path: '/privacy', element: <PrivacyPolicy /> },
        { path: '/recruitment', element: <RecruitmentForm /> },
      ],
    },
    // Protected routes
    {
      element: <ProtectedRoute />,
      children: [
        // Header only (no sidebar)
        {
          element: <HeaderOnlyLayoutWrapper />,
          children: [
            {
              element: <ProjectsRoute />,
              children: [
                { path: '/projects/:projectId/gantt-new', element: <GanttNewPage /> },
              ],
            },
          ],
        },
        // Main layout with sidebar
        {
          element: <MainLayoutWrapper />,
          children: [
            { path: '/dashboard', element: <Dashboard /> },
            { path: '/pm-dashboard', element: <ProjectManagerDashboard /> },
            {
              element: <ProjectsRoute />,
              children: [
                { path: '/projects', element: <Projects /> },
                { path: '/projects/create', element: <CreateProject /> },
                { path: '/projects/:projectId', element: <ProjectDetail /> },
                { path: '/projects/:projectId/detail', element: <ProjectDetail /> },
                { path: '/projects/:projectId/edit', element: <EditProject /> },
                { path: '/projects/:projectId/gantt', element: <GanttChartPage /> },
              ],
            },
            { path: '/tasks', element: <Tasks /> },
            { path: '/teams', element: <Teams /> },
            { path: '/materials', element: <Materials /> },
            { path: '/budget', element: <Budget /> },
            { path: '/documents', element: <Documents /> },
            { path: '/notifications', element: <Notifications /> },
            // Admin Recruitment
            { path: '/admin/recruitment', element: <AdminRecruitmentList /> },
            { path: '/admin/recruitment/:id', element: <AdminRecruitmentDetail /> },
            // Debug route (dev only)
            ...(process.env.NODE_ENV === 'development' ? [
              { path: '/debug/token', element: <TokenDebug /> },
            ] : []),
            // Progress
            { path: '/progress/update', element: <UpdateProgress /> },
            { path: '/progress/submissions', element: <ProgressSubmissionList /> },
            { path: '/progress/submissions/:submissionId/edit', element: <UpdateProgress /> },
            { path: '/progress/approval', element: <ApprovalProgress /> },
            { path: '/fund-requests', element: <FundRequestList /> },
            { path: '/fund-requests/new', element: <FundRequestForm /> },
            { path: '/fund-requests/:id/edit', element: <FundRequestForm /> },
            { path: '/fund-requests/:id', element: <FundRequestDetail /> },
            { path: '/fund-requests/summary', element: <TransferSummary /> },
            { path: '/rekening', element: <RekeningList /> },
            // Master jobs (CommercialRoute)
            {
              path: '/master-jobs',
              element: (
                <CommercialRoute>
                  <MasterJobList />
                </CommercialRoute>
              ),
            },
            // Settings (SettingsRoute)
            {
              path: '/settings',
              element: (
                <SettingsRoute>
                  <Settings />
                </SettingsRoute>
              ),
              children: [
                { path: 'profile', element: <ProfileSettings /> },
                { path: 'notifications', element: <NotificationSettings /> },
                { path: 'notification-polling', element: <NotificationPollingSettings /> },
                { path: 'language', element: <LanguageSettings /> },
                // Settings/admin (AdminRoute)
                {
                  path: 'admin',
                  element: (
                    <AdminRoute>
                      <AdminPanel />
                    </AdminRoute>
                  ),
                  children: [
                    { path: 'users' },
                    { path: 'roles' },
                    { path: 'projects' },
                    { path: 'clients' },
                    { path: 'production-coordinators' },
                    { path: 'installation-teams' },
                    { path: 'integration' },
                    { path: 'branding' },
                    { path: 'audit' },
                    { path: 'testing' },
                    { path: 'system' },
                    { path: 'blast-notif' },
                  ],
                },
                { index: true, element: <Navigate to="profile" replace /> },
              ],
            },
          ],
        },
      ],
    },
    // Root redirect
    { path: '/', element: <Navigate to="/login" replace /> },
    // Catch-all
    { path: '*', element: <Navigate to="/login" replace /> },
  ]);

  return (
    <AuthProvider>
      <NotificationSettingsProvider>
        <ToastContainer position="top-right" autoClose={5000} />
        <RouterProvider
          router={router}
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        />
      </NotificationSettingsProvider>
    </AuthProvider>
  );
}

export default App;
